/**
 * Initializes the menu functionality by setting up event listeners for menu toggle actions,
 * second-level menu toggle actions, and additional behaviors like outside click handling
 * for closing dialogs or focusing on input fields.
 *
 * @return {void} This method does not return a value.
 */
export default function InitMenu() {
    const openSecondLevel = function (toggleSecondLevel) {
        const firstLevelItem = toggleSecondLevel.closest('.nav-item--first-level');
        const firstLevelList = toggleSecondLevel.closest('.first-level');
        const secondLevel = firstLevelItem?.querySelector('.second-level');

        if (!firstLevelItem || !secondLevel) {
            return;
        }

        firstLevelItem.classList.add('active');
        // NOT the generic .invisible utility (opacity:0) — same reasoning
        // as openThirdLevel below: a parent's opacity applies to its whole
        // rendered subtree, so it would also hide the active item's own
        // nested .second-level, not just the siblings this is meant to hide
        firstLevelList?.classList.add('is-drilled');
        secondLevel.classList.remove('is-collapsed');
    };

    const openThirdLevel = function (toggleThirdLevel) {
        const secondLevelItem = toggleThirdLevel.closest('.nav-item--second-level');
        const secondLevelList = toggleThirdLevel.closest('.second-level-list');
        const secondLevel = toggleThirdLevel.closest('.second-level');
        const thirdLevel = secondLevelItem?.querySelector('.third-level');

        if (!secondLevelItem || !thirdLevel) {
            return;
        }

        secondLevelItem.classList.add('active');
        // NOT the generic .invisible utility (opacity:0) — that would also
        // apply to the active item's own nested .third-level, since a
        // parent's opacity applies to its whole rendered subtree
        // regardless of a descendant's own opacity (compositing, not
        // simple inheritance) — the third-level flyout would silently
        // render at 0 opacity along with everything else being hidden
        secondLevelList?.classList.add('is-drilled');
        // hides .second-level's own back-header (mobile only, see
        // HeaderMobile.scss) — otherwise it overlaps .third-level's own
        // header, both being position:absolute at the same top:0
        secondLevel?.classList.add('is-drilled');
        thirdLevel.classList.remove('is-collapsed');
    };

    const closeThirdLevel = function (toggleThirdLevel) {
        const secondLevelItem = toggleThirdLevel.closest('.nav-item--second-level');
        const secondLevelList = toggleThirdLevel.closest('.second-level-list');
        const secondLevel = toggleThirdLevel.closest('.second-level');
        const thirdLevel = secondLevelItem?.querySelector('.third-level');

        secondLevelItem?.classList.remove('active');
        secondLevelList?.classList.remove('is-drilled');
        secondLevel?.classList.remove('is-drilled');
        thirdLevel?.classList.add('is-collapsed');
    };

    const closeMobileMenu = function (toggleMenu) {
        const icon = toggleMenu.querySelector('.icon');
        const header = toggleMenu.closest('.header');
        const firstLevel = document.querySelector('.first-level');

        icon?.classList.remove('icon-close');
        icon?.classList.add('icon-menu');
        firstLevel?.classList.add('tablet-hidden');
        header?.classList.remove('header--menu-open');
    };

    const toggleMenuButtons = document.querySelectorAll('[data-action="toggleMenu"]');

    toggleMenuButtons.forEach(function (toggleMenu) {
        toggleMenu.addEventListener('click', function () {
            const icon = toggleMenu.querySelector('.icon');
            const header = toggleMenu.closest('.header');
            const firstLevel = document.querySelector('.first-level');

            icon?.classList.toggle('icon-close');
            icon?.classList.toggle('icon-menu');
            firstLevel?.classList.toggle('tablet-hidden');
            header?.classList.toggle('header--menu-open', !firstLevel?.classList.contains('tablet-hidden'));
        });
    });

    // resizing/rotating past the desktop breakpoint while the mobile menu is
    // open leaves it stuck open (only the click handler above ever changes
    // this state) — the desktop nav CSS then applies on top of the still-
    // open mobile layout, breaking into the overlapping-pills mess this
    // fixes. Force the menu closed whenever we're at desktop width.
    window.addEventListener('resize', function () {
        if (!window.matchMedia('(min-width: 992px)').matches) {
            return;
        }

        const firstLevel = document.querySelector('.first-level');
        if (firstLevel && !firstLevel.classList.contains('tablet-hidden')) {
            toggleMenuButtons.forEach(closeMobileMenu);
        }
    });

    document.querySelectorAll('[data-action="toggleSecondLevel"]').forEach(function (toggleSecondLevel) {
        toggleSecondLevel.addEventListener('click', function (event) {
            if (toggleSecondLevel.closest('.second-level__headline')) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            openSecondLevel(toggleSecondLevel);
        });
    });

    // NOT a whole-row/link click handler here (there used to be one on
    // ".nav-item--first-level > .label-container" and its "> a" — both
    // preventDefault'd and opened the second level on any click in the
    // row). Per request, the label text should navigate normally on
    // mobile; only the arrow (data-action="toggleSecondLevel", handled
    // above) opens the sublist.

    document.querySelectorAll('.second-level__headline [data-action="toggleSecondLevel"]').forEach(function (toggleSecondLevel) {
        toggleSecondLevel.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();

            const firstLevelItem = toggleSecondLevel.closest('.nav-item--first-level');
            const firstLevelList = toggleSecondLevel.closest('.first-level');
            const secondLevel = firstLevelItem?.querySelector('.second-level');

            firstLevelItem?.classList.remove('active');
            firstLevelList?.classList.remove('is-drilled');
            secondLevel?.classList.add('is-collapsed');
        });
    });

    document.querySelectorAll('[data-action="toggleThirdLevel"]').forEach(function (toggleThirdLevel) {
        toggleThirdLevel.addEventListener('click', function (event) {
            if (toggleThirdLevel.closest('.third-level__headline')) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            // acts as an actual open/close toggle (not just "open") — on
            // desktop the third-level flyout has no visible back/close
            // button (that's mobile-only, see .third-level__headline in
            // HeaderDesktop.scss), so re-clicking the same arrow is the
            // only way to close it there
            const secondLevelItem = toggleThirdLevel.closest('.nav-item--second-level');
            if (secondLevelItem?.classList.contains('active')) {
                closeThirdLevel(toggleThirdLevel);
            } else {
                openThirdLevel(toggleThirdLevel);
            }
        });
    });

    // same change as the first/second-level pair above: no whole-row/link
    // handler here — only the arrow (data-action="toggleThirdLevel",
    // handled above) opens the third level; the label text navigates.

    document.querySelectorAll('.third-level__headline [data-action="toggleThirdLevel"]').forEach(function (toggleThirdLevel) {
        toggleThirdLevel.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();

            closeThirdLevel(toggleThirdLevel);
        });
    });

    // enter-document-code dialogs elsewhere on the page (e.g. the header button) still use the
    // checkbox-toggle pattern: close on outside click, focus the input when opened
    document.querySelectorAll('.invisible-toggle-content.download-input').forEach(function (dialog) {
        const container = dialog.parentElement;
        const toggle = container?.querySelector('.invisible-toggle');
        const label = toggle ? container.querySelector('label[for="' + toggle.id + '"]') : null;
        if (!toggle) return;

        window.addEventListener('click', function (event) {
            const path = event.composedPath();
            if (toggle.checked && !path.includes(dialog) && !path.includes(label)) {
                toggle.checked = false;
            }
        }, true);

        toggle.addEventListener('change', function () {
            if (toggle.checked) {
                dialog.querySelector('.input__field')?.focus();
            }
        });
    });

    document.querySelectorAll('[data-action="scrollTop"]').forEach(function (button) {
        button.addEventListener('click', function () {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    });

    // header search: desktop slides out; mobile expands inline inside the menu
    document.querySelectorAll('.header-search').forEach(function (headerSearch) {
        const searchInput = headerSearch.querySelector('.header-search__input');
        const searchSubmit = headerSearch.querySelector('.header-search__submit');

        if (!searchInput || !searchSubmit) {
            return;
        }

        // the magnifier doubles as toggle: first click opens the panel, afterwards it submits
        searchSubmit.addEventListener('click', function (event) {
            if (!headerSearch.classList.contains('is-open')) {
                event.preventDefault();
                headerSearch.classList.add('is-open');
                searchInput.focus();
            }
        });

        searchInput.addEventListener('click', function () {
            headerSearch.classList.add('is-open', 'is-expanded');
        });

        searchInput.addEventListener('focus', function () {
            headerSearch.classList.add('is-open', 'is-expanded');
        });

        document.addEventListener('click', function (event) {
            if (!headerSearch.contains(event.target)) {
                headerSearch.classList.remove('is-open', 'is-expanded');
            }
        });

        const scopeInput = headerSearch.querySelector('.header-search__scope-input');
        const searchForm = headerSearch.querySelector('.header-search__form');
        const documentsHint = headerSearch.querySelector('[data-documents-hint]');
        const minLengthWarning = headerSearch.querySelector('[data-min-length-warning]');
        const MIN_SEARCH_LENGTH = 3;
        const allChips = headerSearch.querySelectorAll('.header-search__scope[data-scope-value], .header-search__scope[data-documents-mode]');

        // only applies to the product/page search input - "Documents" mode
        // looks up an exact document code, which can be shorter. An empty
        // input counts as "too short" too - no more "browse the whole
        // catalog" via submitting a blank search.
        const updateMinLengthWarning = function () {
            if (!minLengthWarning) {
                return;
            }
            const length = searchInput.value.trim().length;
            const tooShort = searchInput.name === 'search' && length < MIN_SEARCH_LENGTH;
            minLengthWarning.hidden = !tooShort;
        };

        searchInput.addEventListener('input', updateMinLengthWarning);

        if (searchForm) {
            searchForm.addEventListener('submit', function (event) {
                const length = searchInput.value.trim().length;
                if (searchInput.name === 'search' && length < MIN_SEARCH_LENGTH) {
                    event.preventDefault();
                    updateMinLengthWarning();
                }
            });
        }

        const activateChip = function (chip) {
            allChips.forEach(function (other) {
                other.classList.remove('header-search__scope--active');
            });
            chip.classList.add('header-search__scope--active');
        };

        // the CMO/CDMO chip is a plain link (not a search filter) and navigates away instead
        headerSearch.querySelectorAll('.header-search__scope[data-scope-value]').forEach(function (chip) {
            chip.addEventListener('click', function () {
                activateChip(chip);
                // leaving "Documents" mode (if it was active): restore the normal product search
                if (searchForm && searchForm.dataset.searchUrl) {
                    searchForm.action = searchForm.dataset.searchUrl;
                }
                searchInput.name = 'search';
                searchInput.placeholder = searchInput.dataset.searchPlaceholder || searchInput.placeholder;
                if (scopeInput) {
                    scopeInput.value = chip.dataset.scopeValue || '';
                }
                if (documentsHint) {
                    documentsHint.hidden = true;
                }
                updateMinLengthWarning();
                searchInput.focus();
            });
        });

        // "Documents" chip: same input/button, just re-targets the form to the
        // document-code lookup page instead of running a product search
        const documentsChip = headerSearch.querySelector('.header-search__scope[data-documents-mode]');
        if (documentsChip && searchForm) {
            documentsChip.addEventListener('click', function () {
                activateChip(documentsChip);
                searchForm.action = searchForm.dataset.downloadUrl;
                searchInput.name = 'code';
                searchInput.placeholder = searchInput.dataset.downloadPlaceholder || searchInput.placeholder;
                if (scopeInput) {
                    scopeInput.value = '';
                }
                if (documentsHint) {
                    documentsHint.hidden = false;
                }
                if (minLengthWarning) {
                    minLengthWarning.hidden = true;
                }
                searchInput.focus();
            });
        }
    });
}
